import { TagDescriptor } from "@/components/app/layout/kanban/tagDescriptor";
import { ehListaEnviadaGravadaComoGenerica, lerListaEnviada } from "@/utils/listaDeOpcoes";
import { ehVideoRecado } from "@/utils/videoRecado";
import { AnuncioData, CatalogOrderData, CatalogOrderItem, CompartilhamentoData, Atendente, Chat, ChatMessageType, ChatParticipant, ChatReadStatus, ChatStatus, ContactData, EventData, FileData, GroupInviteData, LastMessage, ListData, ListOption, LocalizationData, Message, PollData, PollOption } from "@/mocks/app/mockChatData";
import { FullInfoAtendimento, WhizLastMessageInfo } from "@/services/atendimentos/atendimentos.service";
import { IdAtendimento, IdGrupo, makeIdEtiqueta } from "@/services/common-types";
import { GrupoService, InfoMensagemGrupo, ResponseGrupo } from "@/services/grupos/grupos.service";
import { getDisplayTextForMessageContent, GroupInviteContent, MensagensService, MessageContent, MultipleContactsContent, ReceivedContentBase, ReceivedDocumentContent, ListContent, ReceivedPixContent, ReceivedPollContent, ReceivedReactionContent, ReceivedTextContent, ResponseMessage, SentAudioContent, SentContentBase, SentDocumentContent, SentImageContent, SentPixContent, SentVideoContent, SingleContactContent, WhizMessageAtendimento, WhizMessageSituacao, WhizMessageTipo, ListAnswerContent, WhizMessageGrupo, ListContentSecao, LocalizationContent, InteractiveContent, WhizMessageDadosResposta, UnsupportedContent } from "@/services/mensagens/mensagens.service";
import { IMetaTemplate, IMetaTemplateNamedParams, IMetaTemplatePositionalParams } from "@/services/templates-wpp-meta/meta-wpp-template.service";
import { ResponseUsuario } from "@/services/usuarios/usuarios.service";
import { getRandomInt } from "@/utils/getRandomLetter";
import { mensagemOcultaPorExclusao } from "@/utils/mensagemOculta";
import { normalizarPhone } from "@/utils/phone";

export type DiscriminatedChat = Chat & { itemType: 'chat'|'group' }

/**
 * Chaves de story que o servidor acrescenta ao `conteudo` das mensagens de
 * Instagram. São strings porque o `conteudo` é um mapa livre no Cassandra
 * (`'1'` faz o papel de booleano).
 */
export type StoryContent = MessageContent & {
    ehStoryMention?: string;
    ehRespostaStory?: string;
    /** Story que o CLIENTE compartilhou no direct (anexo `ig_story`). */
    ehCompartilhamentoStory?: string;
    storyId?: string;
    storyUrl?: string;
    /**
     * Vencimento (ISO) da URL de CDN quando o servidor NÃO conseguiu re-hospedar
     * a mídia. A URL de story da Meta morre com o story (~24h) e depois disso
     * responde erro: passada a data, a bolha mostra "story expirado" em vez de
     * oferecer um link quebrado. Ausente quando a cópia é nossa (não expira).
     */
    midiaExpiraEm?: string;
    /** Idem, para a miniatura do story respondido (`storyUrl`). */
    storyExpiraEm?: string;
};

/**
 * Publicação do Instagram compartilhada no direct (post ou reel). Quando o
 * servidor consegue re-hospedar o arquivo, a mensagem é mídia normal; quando só
 * há o permalink, ele manda este cartão — antes disso o permalink era baixado
 * como se fosse mídia e a mensagem virava um "anexo binário" com um HTML dentro.
 */
export type CompartilhamentoContent = MessageContent & {
    /** String JSON na prática (MAP<TEXT,TEXT>), como `anuncio`. */
    compartilhamento?: CompartilhamentoData | string;
};

/**
 * Anúncio CTWA que o servidor enxerta no `conteudo` da primeira mensagem do lead
 * (`contextInfo.externalAdReply` do WhatsApp). Objeto, não chaves planas como o
 * story — ver `docs/specs/anuncio-ctwa.md` no whiz-server.
 */
export type AnuncioContent = MessageContent & {
    /** String JSON na prática (MAP<TEXT,TEXT>); objeto só antes de serializar. */
    anuncio?: AnuncioData | string;
};

/**
 * Pedido do catálogo do WhatsApp. `orderMessage` é o nó CRU do baileys: o
 * servidor não o desmonta, só repassa. Chega como string JSON porque `conteudo`
 * é MAP<TEXT,TEXT> no Cassandra — mesmo tratamento de `anuncio` e
 * `compartilhamento`.
 */
export type PedidoCatalogoContent = MessageContent & {
    orderMessage?: OrderMessageCru | string;
    /** Transporte OFICIAL da Meta (Cloud API): `type: "order"`. Outra forma. */
    order?: MetaOrderCru | string;
};

/**
 * Nó `order` da Cloud API oficial. Forma bem diferente do `orderMessage` do
 * baileys: traz os ITENS, mas não traz nome de produto, miniatura, total pronto,
 * `orderId` nem `status`.
 */
export type MetaOrderCru = {
    /** Nota livre do comprador. */
    text?: string;
    catalog_id?: string;
    product_items?: MetaOrderItemCru[];
};

export type MetaOrderItemCru = {
    /** SKU — o ÚNICO identificador do produto que a Meta manda. */
    product_retailer_id?: string;
    quantity?: number;
    /**
     * Preço UNITÁRIO já na unidade da moeda: `18` é R$ 18,00.
     * ⚠️ NÃO dividir por 1000 — isso é regra do `totalAmount1000` do baileys.
     */
    item_price?: number;
    currency?: string;
};

/** Nó `orderMessage` como o WhatsApp o manda. Todo campo é opcional na prática. */
export type OrderMessageCru = {
    orderId?: string;
    orderTitle?: string;
    itemCount?: number | string;
    message?: string;
    /** String no wa-go (protojson); número do enum proto na Evolution. */
    status?: string | number;
    surface?: string | number;
    /**
     * JPEG base64 SEM o prefixo `data:`; é a capa do produto. A Evolution manda
     * os bytes como objeto indexado — ver `ToMiniaturaDataUrl`.
     */
    thumbnail?: unknown;
    /**
     * Total em MILÉSIMOS da unidade menor da moeda: `"69000"` é R$ 69,00.
     * String no wa-go; na Evolution chega como Long `{low, high, unsigned}`.
     */
    totalAmount1000?: string | number | LongCru;
    totalCurrencyCode?: string;
};

/** `Long` do protobufjs serializado em JSON (Evolution). */
type LongCru = { low: number; high: number; unsigned?: boolean };

/** Enum proto `OrderMessage.OrderStatus` — a Evolution manda o número. */
const STATUS_PEDIDO_PROTO: Record<number, string> = { 1: 'INQUIRY', 2: 'ACCEPTED', 3: 'DECLINED' };
/** Enum proto `OrderMessage.OrderSurface`. */
const SURFACE_PEDIDO_PROTO: Record<number, string> = { 1: 'CATALOG' };

function LongParaNumero(valor: string | number | LongCru | undefined): string | number | undefined {
    if (valor && typeof valor === 'object') {
        if (typeof valor.low !== 'number' || typeof valor.high !== 'number') return undefined;
        return valor.high * 2 ** 32 + (valor.low >>> 0);
    }
    return valor;
}

export type OptionalString = string | undefined;
export type OptionalNumber = number | undefined;
export type OptionalBool = boolean | undefined;

export function FilterChat(chat: Chat, text?: string, tags?: TagDescriptor[]): boolean {
    const validTextFilter = !(!text || text.length == 0);
    const validTagFilter = !(!tags || tags.length == 0);

    const aIncludesB = (a: string, b: string) => a.toLowerCase().includes(b.toLowerCase());
    const textFilterMatch = validTextFilter ? aIncludesB(chat.name, text) || aIncludesB(chat.lastMessage.text, text) : true;

    const chatHasTags = chat.tags.length > 0;
    const chatTagMap = chatHasTags ? chat.tags.reduce((map, tag) => map.set(tag.id, true), new Map<string, true>()) : new Map();
    const tagFilterMatch = validTagFilter && chatHasTags ? tags.some(t => chatTagMap.has(t.id)) : true;

    return textFilterMatch || tagFilterMatch;
}

export function extractInitials(input: string) { return input.slice(0,3).toUpperCase() }

/**
 * Lê o `listHeader` do card Pix (`**Chave PIX**` / `Nome: …` / `Chave: …` / corpo).
 * Titular e chave saem SÓ das linhas até `Chave:` — o corpo da cobrança vem
 * depois e pode conter "Nome:"/"Sobrenome:", que não podem virar titular.
 */
export function extrairCamposPix(header: string | undefined | null): { titular: string, chave: string, corpo: string } {
    const texto = header ?? '';
    const linhaChave = texto.match(/^[ \t]*Chave:[ \t]*(.*)$/im);
    if (!linhaChave || linhaChave.index === undefined) return { titular: '', chave: '', corpo: '' };
    const limpar = (v?: string) => (v ?? '').replace(/\*/g, '').trim();
    const campos = texto.slice(0, linhaChave.index);
    return {
        titular: limpar(campos.match(/^[ \t]*Nome:[ \t]*(.*)$/im)?.[1]),
        chave: limpar(linhaChave[1]),
        corpo: texto.slice(linhaChave.index + linhaChave[0].length).trim(),
    };
}

/**
 * Chave de votante de um voto NOSSO em `conteudo.votos`.
 *
 * Espelha `VOTANTE_PROPRIO` do servidor: em 1:1 o evento de voto só carrega o
 * `remoteJid` do OUTRO lado, então o voto próprio não tem número para chavear —
 * gravar aquele número faria o nosso voto sobrescrever o do contato.
 */
export const CHAVE_VOTANTE_PROPRIO = 'me';

/**
 * Prefixo da chave de votante quando a plataforma só revelou o `@lid`.
 *
 * Espelha `PREFIXO_VOTANTE_LID` do servidor: os dígitos de um `@lid` não são
 * telefone e poderiam coincidir com o número de outra pessoa no mesmo mapa.
 */
export const PREFIXO_VOTANTE_LID = 'lid:';

/**
 * Forma canônica de uma chave de votante. Espelha `chaveCanonica` do servidor.
 *
 * A guarda de dígitos puros é o que deixa `me`, `lid:<dígitos>` e o sufixo
 * `-group` passarem intactos: `normalizarPhone` começa com `replace(/\D/g,'')`
 * e, quando o número não é BR, devolve esse resultado JÁ SEM os não-dígitos —
 * não a entrada.
 */
export function chaveCanonicaDeVotante(chave: string): string {
    return /^\d+$/.test(chave) ? normalizarPhone(chave) : chave;
}

/**
 * Recolhe o mapa de votos para chaves canônicas.
 *
 * **Sem isto o mesmo humano conta duas vezes.** A chave do votante é identidade
 * de estado durável no Cassandra e, enquanto o servidor gravava o número CRU,
 * quem tem número BR legado ficou sob 12 dígitos; hoje o servidor grava sob 13.
 * O servidor recolhe o mapa dele na leitura, mas só PERSISTE o recolhimento no
 * caminho de escrita aceita — uma enquete que não receba mais nenhum voto fica
 * com as duas chaves na linha para sempre, e o front, que agrega o mapa cru,
 * mostrava a mesma pessoa duas vezes no popover e somava 2 em `totalVoters`.
 *
 * Roda sobre o mapa INTEIRO, lápide incluída, antes de filtrar os ativos: uma
 * lápide sob a chave legada com `ts` maior tem que vencer um voto ativo sob a
 * canônica com `ts` menor — é o que o servidor faz, e divergir aqui traria de
 * volta o voto que a pessoa retirou.
 *
 * Empate de `ts` mantém a primeira; fora do empate fica a MAIOR, que é a seleção
 * corrente e a marca d'água de ordem válida. (`Number(… ?? 0)` porque o mapa vem
 * de `JSON.parse` de um TEXT do Cassandra; o servidor compara `ts` cru, mas ele
 * recusa gravar voto sem `senderTimestampMs`, então entrada sem `ts` não existe
 * pelo caminho normal — a coerção é só para não deixar `NaN` decidir.)
 *
 * NÃO recalcula contagem: `votosContagem` é o que o evento ao vivo atualiza, e
 * recontar aqui faria o card ignorar o voto que acabou de chegar.
 *
 * **A chave de saída é a ORIGINAL da entrada vencedora, não a canônica.** O
 * recolhimento existe para deduplicar e contar, não para reescrever identidade:
 * quem nomeia o votante é o card, e ele procura o contato em `contatosByCodigo`
 * por igualdade de string, tentando a chave CRUA e a normalizada — porque a
 * tabela de contatos também tem linha antiga sem normalizar. Devolver a chave
 * canônica tornaria as duas buscas idênticas e o contato legado voltaria a ficar
 * sem nome, desfazendo em silêncio a correção que essa busca dupla é.
 */
export function canonizarVotosDeEnquete<T extends { ts?: unknown }>(
    votos: Record<string, T>,
): Record<string, T> {
    // canônica -> [chave original vencedora, entrada]
    const vencedores = new Map<string, [string, T]>();
    for (const [chave, entrada] of Object.entries(votos)) {
        const canonica = chaveCanonicaDeVotante(chave);
        const existente = vencedores.get(canonica);
        if (!existente || Number(entrada?.ts ?? 0) > Number(existente[1]?.ts ?? 0)) {
            vencedores.set(canonica, [chave, entrada]);
        }
    }
    const saida: Record<string, T> = {};
    for (const [chave, entrada] of vencedores.values()) saida[chave] = entrada;
    return saida;
}

export class MakeLastMessage {
        static fromAtendimento(idAtendimento: IdAtendimento, ultimaMensagemInfo: WhizLastMessageInfo, lastMessagePlaceholder?: string) {
        // Ligação chega como `GENERICA` com o invólucro `ligacao` no conteúdo (o
        // servidor não pode gravar um valor de enum novo sem quebrar a leitura
        // dos pods da versão anterior). Promover aqui é o que dá ao card o
        // ícone de telefone e tira o check de enviado — mesmo padrão do evento
        // de agenda.
        const ehLigacao = !!(ultimaMensagemInfo.conteudo as { ligacao?: unknown } | undefined)?.ligacao;

        const lastMessage: LastMessage = {
            sender: TextContentUtil.extractSignature(ultimaMensagemInfo.conteudo) || null,
            text  : getDisplayTextForMessageContent(ultimaMensagemInfo.conteudo, ultimaMensagemInfo.tipo) || lastMessagePlaceholder || '',
            type  : "text" as ChatMessageType,
            ogType: ehLigacao ? 'LIGACAO' : ultimaMensagemInfo.tipo,
            // Sem isFromMe, ChatItem.showMessageStatus fica false e o check de enviado
            // some ao remontar a lista da API (refresh). enviadaPorMim já vem no JSONB.
            isFromMe: ultimaMensagemInfo.enviadaPorMim,
            // situacao persistida no JSONB reflete o mesmo check exibido ao vivo
            // (enviado/entregue/lido). Fallback 'sent' para atendimentos antigos.
            status: ultimaMensagemInfo.situacao
                ? MakeMessage.ReadStatus.FromWhizSituacao(ultimaMensagemInfo.situacao)
                : "sent"
        };

        //if (ultimaMensagemInfo.enviadaPorMim) void this.fetchAndUpdateReadStatusFromAtendimentoIfIsSent(idAtendimento, lastMessage);
        return lastMessage;
    }

    private static async fetchAndUpdateReadStatusFromAtendimentoIfIsSent(idChat: IdAtendimento, lastMessage: LastMessage) { return await this.fetchAndUpdateReadStatusIfIsSent(idChat, 'ATENDIMENTO', lastMessage)}
    private static async fetchAndUpdateReadStatusFromGrupoIfIsSent(idChat: IdGrupo, lastMessage: LastMessage) { return await this.fetchAndUpdateReadStatusIfIsSent(idChat, 'GRUPO', lastMessage)}
    private static async fetchAndUpdateReadStatusIfIsSent(idChat: IdAtendimento|IdGrupo, type: 'ATENDIMENTO'|'GRUPO', lastMessage: LastMessage) {
        let responseMessage: ResponseMessage;

        switch (type) {
            case "ATENDIMENTO": responseMessage = await new MensagensService().getMensagensByAtendimento(idChat as IdAtendimento, 1, 1); break;
            case "GRUPO": responseMessage = await new MensagensService().getMensagensByGrupo(idChat as IdGrupo, undefined, 1); break;
        }
        const actualLastMessage = responseMessage.items.at(0)

        lastMessage.isFromMe = actualLastMessage?.enviadaPorMim;
        if (actualLastMessage && actualLastMessage.enviadaPorMim) {
            lastMessage.status = MakeMessage.ReadStatus.FromWhizSituacao(actualLastMessage.situacao)
        }
    }

    static _oldFromAtendimento(idAtendimento: IdAtendimento, ultimaMensagem?: string|null, ultimoTipoMensagem?: WhizMessageTipo|null, lastMessagePlaceholder?: string) {
        const lastMessage: LastMessage = {
            sender: TextContentUtil.extractSignature({ texto: ultimaMensagem || ''}) || null,
            text  : getDisplayTextForMessageContent({ mensagem: ultimaMensagem, reacao: ultimoTipoMensagem }, ultimoTipoMensagem ?? undefined) || lastMessagePlaceholder || '',
            type  : "text" as ChatMessageType,
            ogType: ultimoTipoMensagem,
            status: "sent"
        };

        void this.fetchAndUpdateReadStatusFromAtendimentoIfIsSent(idAtendimento, lastMessage);
        return lastMessage;
    }

    static fromGrupo(idGrupo: IdGrupo, infoMensagemGrupo?: InfoMensagemGrupo|null, lastMessagePlaceholder?: string): LastMessage | undefined {
        if (!infoMensagemGrupo) return { sender: null, text: lastMessagePlaceholder || '', type: 'text' };
        const lastMessage: LastMessage =  {
            sender: infoMensagemGrupo?.enviadaPorMim ? TextContentUtil.extractSignature(infoMensagemGrupo.conteudo) || null : (infoMensagemGrupo?.contato?.nome ?? infoMensagemGrupo?.contato?.codigo),
            text: getDisplayTextForMessageContent(infoMensagemGrupo.conteudo, infoMensagemGrupo.tipo),
            type: "text" as ChatMessageType,
            ogType: infoMensagemGrupo?.tipo,
            isFromMe: infoMensagemGrupo?.enviadaPorMim,
            status: "sent"
        };

        //if (infoMensagemGrupo.enviadaPorMim) void this.fetchAndUpdateReadStatusFromGrupoIfIsSent(idGrupo, lastMessage);
        return lastMessage;
    }
}

export class MakeChat {
    static FromAtendimento(fullAtendimento: FullInfoAtendimento): Chat {
        const datetimeChat = new Date(fullAtendimento.ultimaInteracao ?? fullAtendimento.dataCriacao);
        const offsetMinutes = datetimeChat.getTimezoneOffset();
        if (offsetMinutes === 0) {
            datetimeChat.setHours(datetimeChat.getHours() - 3);
        }

        const status: ChatStatus     = fullAtendimento.dataFinalizacao ? 'Finalizado' : fullAtendimento.dataAbertura ? 'Aberto' : 'Criado';
        const atendentes = fullAtendimento.atendentes.map(a => ({ id: a.id, name: a.nome, initials: extractInitials(a.nome) }))

        const lastMessagePlaceholder = status == "Aberto" ? 'Atendimento Aberto' : status == "Criado" ? 'Atendimento Criado' : 'Atendimento Finalizado';
        const lastMessage: LastMessage = fullAtendimento.ultimaMensagemInfo ? MakeLastMessage.fromAtendimento(fullAtendimento.id, fullAtendimento.ultimaMensagemInfo, lastMessagePlaceholder) : { sender: null, text: lastMessagePlaceholder, type: 'text' };

        const chatObj: Chat = { ...fullAtendimento, ...{
            name  : fullAtendimento.contato.nome   ?? fullAtendimento.contato.codigo,
            avatar: fullAtendimento.contato.imagem ?? '',

            isBotActive: !!fullAtendimento.idBot,
            idBotAtivo : fullAtendimento.idBot ?? null,

            lastClientInteraction: fullAtendimento.ultimaInteracaoCliente,
            lastMessage: lastMessage,
            time       : datetimeChat.toISOString(),
            unreadCount: fullAtendimento?.naoLidas || 0,
            // undefined quando o payload não incluiu não lidas (preserva local no upsert)
            isMarkedUnread: fullAtendimento?.marcadoNaoLido,

            tags      : fullAtendimento?.contato?.etiquetas ?? [],      // 🚧 N/A
            isPinned: fullAtendimento.isPinned ?? false,
            isFavorite: false,   // 🚧 N/A
            isGroup: false,
            isMuted: false,

            status: status,

            obsContato        : fullAtendimento.contato?.observacao ?? undefined,
            codigoContato     : fullAtendimento.contato?.codigo,
            // Valor de `{nome_whatsapp}` quando um template é escolhido no chat. Nem todo
            // payload de evento traz o campo, então `undefined` aqui significa "não veio" —
            // `__upsertChat` preserva o valor local nesse caso em vez de apagá-lo.
            nomeWhatsappContato: fullAtendimento.contato?.nomeWhatsapp || undefined,
            idContato         : fullAtendimento.contato?.id,
            idClienteAxion    : fullAtendimento.contato?.idClienteAxion ?? undefined,
            nomeClienteAxion  : fullAtendimento.contato?.nomeClienteAxion ?? undefined,
            idResponsavelAxion: fullAtendimento.contato?.idResponsavelAxion ?? undefined,
            idQuadro     : fullAtendimento.idQuadro ?? null,
            idCanal      : fullAtendimento.idCanal ?? null,
            idInbox      : fullAtendimento.idInbox,
            atendentes   : atendentes,

            idChatBotIa : fullAtendimento.idChatBotIa,
            idContaBotIa: fullAtendimento.idContaBotIa,
            statusIntegracaoIa: fullAtendimento.statusIntegracaoIa,

            // `status` acima é o ChatStatus derivado de datas e não distingue
            // CRIADO_POR_DISPARO de CRIADO. Guardamos o status cru à parte porque é
            // ele que decide a ocultação do atendimento de campanha.
            // Sem `?? null`: o servidor sempre manda a chave (null quando não há
            // vínculo), então `undefined` aqui significa "payload não trouxe o campo"
            // e o upsert preserva o valor local em vez de apagá-lo.
            statusServidor: fullAtendimento.status,
            disparo       : fullAtendimento.disparo,

            _original    : fullAtendimento
        }};

        if (fullAtendimento.ultimoTipoMensagem == 'GENERICA' && (fullAtendimento.ultimaMensagemInfo?.conteudo as {albumMessage: string})?.albumMessage) {
            void MakeMessage.Async.FetchLastMessageContentUntilCanParse(chatObj, fullAtendimento.atendentes)
        }

        return chatObj;
    }

    static FromGrupo(grupo: ResponseGrupo): Chat {
        const status = 'Aberto';
        const atendentes = [] as Atendente[]; // todo: São os atendentes do inbox de onde o grupo se origina. Talvez seja interessante retornar a lista de atendentes aqui?
        const chatObj: Chat = {
            id: grupo.id,
            name  : MakeChat.nomeExibicaoGrupo(grupo),
            // Foto da comunidade em tamanho normal no grupo de avisos: ele representa a
            // comunidade, e é assim que ela ganha presença visual na lista — a linha
            // dela nunca aparece. A do próprio grupo fica como reserva.
            avatar: (grupo.ehGrupoAvisos ? grupo.comunidade?.imagem : null) || grupo.imagem || '',
            description: grupo.descricao || null,

            lastMessage: MakeLastMessage.fromGrupo(grupo.id, grupo.ultimaMensagemInfo, 'Grupo Criado'),
            time       : grupo.ultimaMensagemData ?? null,  // todo: grupo.dataCriacao,
            unreadCount: grupo.naoLidas ?? 0,               // todo: fullInfoAtendimento?.naoLidas || 0,
            isMarkedUnread: grupo.marcadoNaoLido,

            tags      : (grupo.etiquetas ?? []).map(e => ({ ...e, id: makeIdEtiqueta(e.id) })),
            isMuted   : grupo.silenciado ?? false,
            silenciadoAte: grupo.silenciadoAte ?? null,
            isPinned  : grupo.isPinned ?? false,
            isFavorite: false,   // 🚧 N/A
            isGroup: true,
            isWhitelistEnabled: grupo.whitelistEnabled,
            somenteAdmins: grupo.somenteAdmins,
            ehGrupoAvisos: grupo.ehGrupoAvisos,
            comunidade: grupo.comunidade
                ? { id: grupo.comunidade.id, nome: grupo.comunidade.nome, avatar: grupo.comunidade.imagem }
                : null,
            grupoWhitelist: grupo.whitelist || undefined,

            status: status,

            obsContato   : grupo.descricao,
            codigoContato: undefined,
            idCanal      : undefined,
            idContato    : undefined,
            idQuadro     : null,
            idInbox      : grupo.idInbox,
            codigoGrupo  : grupo.codigo,
            atendentes   : atendentes,
            participants : [],
            // Grupo não tem atendimento: nunca é ocultado nem recebe badge de campanha.
            statusServidor: undefined,
            disparo      : null,
            _original    : grupo
        } as Chat;

        if (grupo.ultimaMensagemInfo?.tipo == 'GENERICA' && (grupo.ultimaMensagemInfo?.conteudo as {albumMessage: string})?.albumMessage) {
            void MakeMessage.Async.FetchLastMessageContentUntilCanParse(chatObj)
        }

        return chatObj;
    }

    /**
     * Normaliza texto para comparação de busca: sem acento, minúsculo, e sem
     * separadores.
     *
     * Descartar `-`, travessões e espaços é o que faz "escola x" encontrar
     * "Escola X - Avisos" — nome composto do grupo de avisos.
     */
    static normalizarBusca(texto: string): string {
        return (texto ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[-\u2013\u2014\s]/g, '');
    }

    /**
     * Um chat corresponde ao termo buscado?
     *
     * Casa nome, código do contato e — para grupo de comunidade — o nome da
     * COMUNIDADE. Sem o último, procurar pela comunidade encontrava só o grupo de
     * avisos (que carrega o nome dela no próprio título) e nenhum dos outros
     * subgrupos, que é justamente onde a busca é útil.
     *
     * Vive aqui, e não copiado nos componentes, porque a lista e a contagem das abas
     * usam o MESMO predicado: quando divergiam, a badge dizia um número e as linhas
     * visíveis eram outras.
     */
    static chatCorrespondeABusca(chat: Chat, termoNormalizado: string): boolean {
        if (!termoNormalizado) return true;

        const candidatos = [chat.name, chat.codigoContato, chat.comunidade?.nome];
        return candidatos.some(c => c && MakeChat.normalizarBusca(c).includes(termoNormalizado));
    }

    /**
     * Rótulo de um participante que ninguém conseguiu identificar.
     *
     * Discriminado pelos últimos dígitos do identificador porque este rótulo também vira
     * nome de remetente nas bolhas e semente da cor: um texto igual para todos colapsaria
     * pessoas distintas no mesmo nome e na mesma cor.
     */
    static rotuloParticipanteSemNome(codigo: string): string {
        const finais = (codigo ?? '').replace(/\D/g, '').slice(-4);
        return finais ? `Participante ···${finais}` : 'Participante';
    }

    /**
     * O participante pode ser mencionado?
     *
     * Não-identificado fica fora: a plataforma não revelou o telefone, então a menção
     * não endereça ninguém, e o rótulo é o mesmo para todos — casaria com qualquer
     * busca e entupiria a lista.
     *
     * Vive aqui porque há DOIS autocompletes de menção (campo de mensagem e legenda de
     * arquivo). Quando o predicado morava só num deles, digitar `@` no modal listava as
     * entradas que o outro já excluía.
     */
    static participantePodeSerMencionado(participante: ChatParticipant): boolean {
        // `isLid` e não `isAnonymous`: com o telefone redigido do WhatsApp o
        // participante ganha rótulo e volta a ser exibível, mas o endereço continua
        // sendo um LID — mencioná-lo não chega a ninguém.
        return !participante.isLid;
    }

    /**
     * Nome de exibição de um grupo.
     *
     * O subgrupo de avisos é apresentado COMO a comunidade — é o que o WhatsApp faz, e
     * é o único jeito de "a comunidade" ser algo que o operador abre: a linha dela é
     * filtrada da listagem. Sem isso, numa lista plana "Avisos" não diz de qual
     * comunidade, e um inbox em várias comunidades vê vários "Avisos" idênticos.
     *
     * Só afeta a EXIBIÇÃO. O nome real continua no servidor: compor aqui evita que o
     * próximo sync de metadados, que sobrescreve `nome` com o valor da plataforma,
     * apague a composição.
     */
    static nomeExibicaoGrupo(grupo: ResponseGrupo): string {
        const nomeComunidade = grupo.comunidade?.nome?.trim();
        const nomeGrupo = grupo.nome?.trim() ?? '';

        if (!grupo.ehGrupoAvisos || !nomeComunidade) return grupo.nome;

        // MESMA normalização da busca (sem acento, sem separador): comparar com uma
        // própria, que mantinha `-` e espaços, fazia "Escola-X" + "Escola X" falhar no
        // `startsWith` e produzir "Escola-X - Escola X" — a duplicação que este método
        // existe para evitar.
        const gNorm = MakeChat.normalizarBusca(nomeGrupo);
        const cNorm = MakeChat.normalizarBusca(nomeComunidade);

        // A marca "Avisos" é o que distingue este subgrupo dos outros da comunidade, e o
        // selo não está lá para ajudar (é suprimido quando o avatar já é a foto dela).
        // Então ela nunca é omitida — só não é repetida.
        const jaMarcaAvisos = gNorm.includes('avisos');

        // O WhatsApp batiza o grupo de avisos com o nome da comunidade por padrão, então
        // "já menciona a comunidade" é o caso COMUM, não exceção — prefixar sem checar
        // produziria "Escola X - Escola X".
        if (gNorm.startsWith(cNorm)) {
            return jaMarcaAvisos ? nomeGrupo : `${nomeGrupo} - Avisos`;
        }

        if (!nomeGrupo) return `${nomeComunidade} - Avisos`;

        return jaMarcaAvisos
            ? `${nomeComunidade} - ${nomeGrupo}`
            : `${nomeComunidade} - ${nomeGrupo} - Avisos`;
    }

    /** Tempo máximo (ms) que a lista de participantes pode ficar em cache antes de ser buscada novamente */
    static readonly PARTICIPANTS_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hora

    static participantsNeedRefresh(chat: Chat): boolean {
        if (!chat.participants || chat.participants.length === 0) return true;
        if (!chat.participantsFetchedAt) return true;
        return (Date.now() - chat.participantsFetchedAt) > MakeChat.PARTICIPANTS_CACHE_TTL_MS;
    }

    static async FetchAndPopulateWithParticipants(chatThatIsGrupo: Chat) {
        try {
            const pp = await new GrupoService().getParticipantes(chatThatIsGrupo.id as IdGrupo);
            // `admin` e `superAdmin` vêm em campos SEPARADOS e mutuamente exclusivos
            // (o adaptador deriva de "admin" vs "superadmin"), então o criador do grupo
            // chega com `admin: false`. Ler só esse campo fazia o dono do grupo passar
            // por participante comum — sem coroa, sem selo, e barrado pela checagem de
            // permissão de envio.
            chatThatIsGrupo.participants = pp.map(p => {
                // Duas condições diferentes, e elas deixaram de coincidir quando o
                // transporte passou a mandar o telefone redigido pelo WhatsApp:
                //   `ehLid`  → não dá para MENCIONAR (a menção não endereça ninguém);
                //   anônimo  → não dá para EXIBIR (não veio contato nem rótulo).
                // Um participante com telefone redigido é exibível e não-mencionável.
                const ehLid = !!p.ehLid;
                const anonimo = ehLid && !p.nome && !p.idContato;

                return {
                    phone: p.codigo,
                    isAdmin: p.admin || p.superAdmin,
                    isSuperAdmin: p.superAdmin,
                    isLid: ehLid,
                    isAnonymous: anonimo,
                    // Rótulo é decisão de exibição. Para LID sem nome o servidor devolve
                    // ausência em vez dos dígitos, justamente para não disfarçar
                    // identificador de nome.
                    //
                    // Os 4 últimos dígitos entram porque o rótulo também alimenta o nome
                    // do remetente nas bolhas e a cor derivada dele: um "Participante"
                    // igual para todos colapsaria pessoas diferentes no mesmo nome e na
                    // mesma cor, e a conversa ficaria ilegível.
                    name: p.nome || (anonimo ? MakeChat.rotuloParticipanteSemNome(p.codigo) : p.codigo),
                    avatar: p.imgUrl ?? undefined,
                    idContato: p.idContato ?? undefined,
                };
            });
            chatThatIsGrupo.participantsFetchedAt = Date.now();
        } catch (e) {
            console.error('[FromGrupo] GetParticipantes failed:');
            console.error(e);
        }
    }
}

/** Forma CRUA de uma enquete no protocolo do WhatsApp, antes de o servidor traduzir. */
export type EnqueteCrua = { name?: string; options?: unknown[]; selectableOptionsCount?: unknown; selectableCount?: unknown };

/**
 * As seis chaves de criação de enquete do protocolo, da mais nova para a mais
 * antiga. `pollCreationMessageV4` é um `FutureProofMessage`: embrulha uma
 * mensagem inteira, então a enquete está um nível abaixo.
 */
const CHAVES_ENQUETE_CRUA = ['pollCreationMessageV6', 'pollCreationMessageV5', 'pollCreationMessageV3', 'pollCreationMessageV2', 'pollCreationMessage'] as const;

/**
 * Acha a enquete crua num `conteudo` que o servidor classificou como GENERICA.
 *
 * Existe só para o HISTÓRICO: mensagens gravadas antes de o servidor aprender as
 * variantes. Mensagem nova chega já como ENQUETE traduzida. Os valores vêm como
 * STRING JSON porque `conteudo` é `MAP<TEXT,TEXT>` no Cassandra.
 */
export function encontrarEnqueteLegada(content: unknown, profundidade = 0): EnqueteCrua|undefined {
    if (!content || typeof content !== 'object' || profundidade > 3) return undefined;
    const mapa = content as Record<string, unknown>;

    for (const chave of CHAVES_ENQUETE_CRUA) {
        const valor = ParseJsonStringOrReturnAsIs(mapa[chave]);
        if (valor && typeof valor === 'object') return valor as EnqueteCrua;
    }

    const futuro = ParseJsonStringOrReturnAsIs(mapa['pollCreationMessageV4']) as { message?: unknown }|undefined;
    if (futuro?.message) return encontrarEnqueteLegada(ParseJsonStringOrReturnAsIs(futuro.message), profundidade + 1);

    return undefined;
}

/**
 * Mesma regra de `permiteMultiplasRespostas` do servidor
 * (`webhooks/shared/poll-creation.util.ts`), para enquete legada cujo `conteudo`
 * ainda está na forma crua: `1` é escolha única, `0` (sem limite) e qualquer
 * valor maior são múltipla, ausente é única. Espelho de propósito — se as duas
 * divergirem, a mesma enquete muda de rótulo dependendo de quando foi gravada.
 */
function legadaPermiteMultiplas(legada: EnqueteCrua): boolean {
    const bruto = legada.selectableOptionsCount ?? legada.selectableCount;
    if (bruto === undefined || bruto === null || bruto === '') return false;
    const quantidade = Number(bruto);
    return Number.isNaN(quantidade) ? false : quantidade !== 1;
}

/**
 * O `conteudo` carrega um pedido do catálogo do WhatsApp?
 *
 * Dois transportes, duas chaves. `orderMessage` (baileys/Evolution) é um nome
 * específico e basta por si. `order` (Cloud API oficial) é genérico demais para
 * servir de marca sozinho, então exige-se a assinatura de um pedido de verdade:
 * `product_items` ou `catalog_id`. Sem isso, qualquer conteúdo futuro com uma
 * chave `order` viraria um card de catálogo vazio.
 */
export function ehPedidoDeCatalogo(content: MessageContent | undefined): boolean {
    const pedido = content as PedidoCatalogoContent | undefined;
    if (pedido?.orderMessage) return true;

    const bruto = pedido?.order;
    if (!bruto) return false;
    // String do Cassandra: olhar as chaves sem pagar o parse completo.
    if (typeof bruto === 'string') return bruto.includes('product_items') || bruto.includes('catalog_id');
    return !!(bruto.product_items || bruto.catalog_id);
}

export class MakeMessage {

    static FromWhizMessage(wMsg: WhizMessageAtendimento|WhizMessageGrupo, info?: { thisUserName?: string, thisChatName?: string }) {
        const isGrupo = !!(wMsg as WhizMessageGrupo).idGrupo;
        const signature = TextContentUtil.extractSignature(wMsg.conteudo);
        // Alguns tipos chegam do servidor como GENERICA mesmo tendo conteúdo tipado (ex.: DOCUMENTO).
        // Resolvemos o tipo efetivo antes de montar a mensagem para não cair no render genérico.
        const tipo = MakeMessage.Tipo.Resolve(wMsg.tipo, wMsg.conteudo);
        const listData = MakeMessage.Content.ToListData(wMsg.conteudo, tipo);
        const sender = !wMsg.enviadaPorMim
            ? isGrupo
                ? (wMsg as WhizMessageGrupo).contato?.nome || signature || info?.thisChatName || undefined
                : signature || info?.thisChatName || undefined
            : (info?.thisUserName && signature && signature == info?.thisUserName)
                ? 'Você'
                : signature || undefined
            ;
        return {
            idExterno: wMsg.idExterno,
            id: wMsg.id,
            // Fora de grupo a assinatura recebida (`*Nome:*\n`, ex.: outro inbox Whiz)
            // vira o cabeçalho `agentName` abaixo — tirá-la do texto evita exibi-la
            // duas vezes. Em grupo o recebido não tem `agentName`, então fica no texto.
            text: TextContentUtil.textMessageWithoutSignature(wMsg.conteudo, tipo, wMsg.enviadaPorMim || !isGrupo) as OptionalString,
            time: wMsg.dataEnvio ?? wMsg.dataCriacao,
            isSent: wMsg.enviadaPorMim,
            isEdited: wMsg.editada,
            isDeleted: wMsg.apagada,
            ehMediaPerdida: wMsg.ehMediaPerdida ?? false,
            // Mídia ainda no reparo assíncrono do servidor. `conteudo` é MAP<TEXT,TEXT>
            // no Cassandra, então a marca chega como a STRING "true".
            midiaPendente: MakeMessage.Content.ToMidiaPendente(wMsg.conteudo),
            miniatura    : MakeMessage.Content.ToMiniaturaDataUrl((wMsg.conteudo as { miniatura?: string } | undefined)?.miniatura),
            // Motivo cru da Meta para um tipo não suportado (erro 131051). Vai
            // só para o tooltip: é texto em inglês, útil para suporte, não para
            // o operador. Ver `UnsupportedContent`.
            motivoNaoSuportada: (wMsg.conteudo as UnsupportedContent | undefined)?.motivo,
            // Motivo da RECUSA de envio, gravado em `conteudo.erro` pelo servidor
            // (`anexarMotivoDaFalha`, no webhook de status da Meta). Vale para qualquer
            // tipo: antes só a bolha de template lia o motivo, e áudio/imagem/vídeo
            // recusados mostravam apenas o ícone vermelho genérico — indistinguível de
            // janela fechada, número inválido ou mídia rejeitada.
            motivoFalha: (wMsg.conteudo as { erro?: string } | undefined)?.erro,
            erroCodigo: (wMsg.conteudo as { erroCodigo?: string } | undefined)?.erroCodigo,
            erroDetalhe: (wMsg.conteudo as { erroDetalhe?: string } | undefined)?.erroDetalhe,
            ehRespostaStatus: wMsg.ehRespostaStatus ?? false,
            ehEncaminhamento: wMsg.ehEncaminhamento ?? false,

            readStatus: MakeMessage.ReadStatus.FromWhizSituacao(wMsg.situacao),
            // `uploadError` é o que liga a afordância de "Reenviar" na bolha. Sem marcá-lo
            // aqui, a falha só era reenviável na sessão em que aconteceu: depois de um F5
            // a mensagem voltava vermelha, mas sem botão.
            // Tipo GRAVADO, não o resolvido: `Tipo.Resolve` promove GENERICA→DOCUMENTO para
            // renderizar, mas o servidor remonta pelo tipo da linha e recusa GENERICA (400).
            uploadError: MakeMessage.PodeReenviar(wMsg, wMsg.tipo),
            type      : MakeMessage.Type.FromWhizMsgTipo(tipo),
            subtype   : MakeMessage.Subtype.FromWhizMsgTipo(tipo),

            sender: sender,
            idUsuarioSender: wMsg.enviadaPorMim ? wMsg.idUsuario : undefined,
            idContatoSender: wMsg.enviadaPorMim ? undefined : isGrupo ? (wMsg as WhizMessageGrupo).contato?.id : wMsg.idContato,
            groupSenderCodigo: isGrupo ? (wMsg as WhizMessageGrupo).contato?.codigo : null,
            groupSenderImg: isGrupo ? (wMsg as WhizMessageGrupo).contato?.imagem : null,
            groupSenderId: isGrupo ? (wMsg as WhizMessageGrupo).contato?.id : null,
            isBot: false as boolean,

            agentName     : isGrupo && !wMsg.enviadaPorMim ? null : isGrupo ? (wMsg as WhizMessageGrupo).contato?.nome ?? TextContentUtil.extractSignature(wMsg.conteudo) : TextContentUtil.extractSignature(wMsg.conteudo) as OptionalString,
            listHeader    : listData?.listHeader as OptionalString,
            listButtonText: listData?.listButtonText as OptionalString,
            listOptions   : listData?.listOptions,
            listInline    : listData?.listInline,

            audioUrl     : MakeMessage.Content.ToFileData(wMsg.conteudo, tipo, wMsg.enviadaPorMim)?.url as OptionalString,
            audioDuration: undefined as OptionalString,
            waveform     : undefined as number[]|undefined,

            pollData: MakeMessage.Content.ToPollData(wMsg.conteudo, tipo),

            eventData: MakeMessage.Content.ToEventData(wMsg.conteudo, tipo),

            replyUrl  : wMsg.dadosResposta?.url as OptionalString,
            replyMiniatura: wMsg.dadosResposta?.url ? undefined : MakeMessage.Content.ToMiniaturaDataUrl(wMsg.dadosResposta?.miniatura),
            replyToId : wMsg.dadosResposta?.id as OptionalString,
            replyType : wMsg.dadosResposta?.tipo ? MakeMessage.Type.FromWhizMsgTipo(wMsg.dadosResposta?.tipo) : undefined,
            replyText : MakeMessage.Content.ToReplyText(wMsg.dadosResposta ?? undefined) as OptionalString,
            replyOwner: wMsg.dadosResposta?.contato?.nome || TextContentUtil.extractSignature({texto: wMsg.dadosResposta?.texto}) as OptionalString,
            replyGrupoNome: wMsg.dadosResposta?.nomeGrupo as OptionalString,

            reactions: new Map() as Map<string,string>|undefined,

            // Contexto de story (Instagram). Vem como chaves planas no `conteudo`
            // porque o conteúdo é um mapa livre no Cassandra. `storyUrl` é a nossa
            // cópia re-hospedada quando o servidor conseguiu baixar — a URL da
            // Meta morre com o story.
            isStoryMention: (wMsg.conteudo as StoryContent)?.ehStoryMention === '1',
            isStoryReply  : (wMsg.conteudo as StoryContent)?.ehRespostaStory === '1',
            isStoryShare  : (wMsg.conteudo as StoryContent)?.ehCompartilhamentoStory === '1',
            storyUrl      : MakeMessage.Content.ToStoryUrl(wMsg.conteudo),
            // Mídia que ficou dependendo da CDN da Meta e já passou da validade:
            // a bolha trava o acesso e diz que expirou, em vez de mostrar um link
            // que responde erro.
            midiaExpirada : MakeMessage.Content.ExpirouMidiaDaMeta(wMsg.conteudo),

            // Post/reel compartilhado que não pôde ser re-hospedado: cartão com
            // link, nunca anexo.
            compartilhamento: MakeMessage.Content.ToCompartilhamento(wMsg.conteudo),

            // Anúncio do Instagram/Facebook que originou o lead (CTWA). Só a
            // primeira mensagem dele traz; é o que identifica o produto divulgado.
            // Chega SERIALIZADO: a coluna `conteudo` é MAP<TEXT,TEXT> no Cassandra e o
            // servidor faz JSON.stringify em todo valor objeto — mesmo tratamento de
            // `secoes`/`botoes`/`opcoes`/`eventMessage`. Sem o parse, o card renderiza
            // vazio (string é truthy, mas `.titulo` é undefined).
            anuncio: MakeMessage.Content.ToAnuncio(wMsg.conteudo),

            isFavorite: undefined as OptionalBool,
            isPinned  : undefined as OptionalBool,
            systemText: undefined as OptionalString,

            legenda: MakeMessage.Content.ToLegenda(wMsg.conteudo),

            fileData        : MakeMessage.Content.ToFileData(wMsg.conteudo, tipo, wMsg.enviadaPorMim),
            contactData     : MakeMessage.Content.ToContactData(wMsg.conteudo, tipo),
            localizationData: MakeMessage.Content.ToLocalizationData(wMsg.conteudo, tipo),
            groupInviteData : MakeMessage.Content.ToGroupInviteData(wMsg.conteudo, tipo),
            // Lido do `conteudo`, não do `tipo`: assim o card aparece igual no
            // histórico gravado como GENERICA e na mensagem nova já tipada.
            catalogOrderData: MakeMessage.Content.ToCatalogOrderData(wMsg.conteudo),

            cursor: wMsg.cursor ?? null,
            location: MakeMessage.Content.ToLocationData(wMsg.conteudo, tipo),

            metaTemplateData: MakeMessage.Content.ToMetaTemplate(wMsg.conteudo, tipo),

            _original: wMsg,
        } as Message
    }

    static Content = {
        /** `true` quando a data ISO já passou. Ausente/ilegível = não expirou. */
        Expirou(iso?: string): boolean {
            if (!iso) return false;
            const t = Date.parse(iso);
            return Number.isFinite(t) && t <= Date.now();
        },
        /**
         * A mídia desta mensagem depende de uma URL de CDN da Meta que já venceu.
         * Só é marcada quando o servidor NÃO conseguiu re-hospedar: com cópia
         * nossa não há vencimento nenhum.
         */
        ExpirouMidiaDaMeta(content: MessageContent | undefined): boolean {
            return MakeMessage.Content.Expirou((content as StoryContent | undefined)?.midiaExpiraEm);
        },
        /** Miniatura do story respondido — some quando a URL da Meta expirou. */
        ToStoryUrl(content: MessageContent | undefined): OptionalString {
            const story = content as StoryContent | undefined;
            if (MakeMessage.Content.Expirou(story?.storyExpiraEm)) return undefined;
            return story?.storyUrl as OptionalString;
        },
        /**
         * `conteudo.compartilhamento` → `CompartilhamentoData`. Mesmo tratamento
         * de `anuncio`: chega como string JSON porque `conteudo` é MAP<TEXT,TEXT>
         * no Cassandra.
         */
        ToCompartilhamento(content: MessageContent | undefined): CompartilhamentoData | undefined {
            const bruto = (content as CompartilhamentoContent | undefined)?.compartilhamento;
            if (!bruto) return undefined;
            const parsed = ParseJsonStringOrReturnAsIs(bruto as unknown as string | object);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? parsed as CompartilhamentoData
                : undefined;
        },
        /** `conteudo.midiaPendente` — "true" (string do Cassandra) ou true (evento ao vivo). */
        ToMidiaPendente(content: MessageContent | undefined): boolean {
            const bruto = (content as { midiaPendente?: unknown } | undefined)?.midiaPendente;
            return bruto === true || bruto === 'true';
        },
        /**
         * Miniatura JPEG do WhatsApp → data URL para `<img>`. Normalmente vem base64,
         * mas a Evolution manda o `Uint8Array` serializado como objeto indexado
         * (`{"0":255,"1":216,...}`) ou `Buffer` (`{type:"Buffer",data:[...]}`) — converte
         * os bytes; qualquer outra coisa é ignorada.
         */
        ToMiniaturaDataUrl(base64?: unknown): string | undefined {
            if (!base64) return undefined;
            if (typeof base64 === 'string') {
                return base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
            }
            if (typeof base64 !== 'object') return undefined;
            const dadosBuffer = (base64 as { data?: unknown }).data;
            const bytes = Array.isArray(dadosBuffer) ? dadosBuffer : Object.values(base64 as Record<string, unknown>);
            if (!bytes.length || !bytes.every(b => typeof b === 'number')) return undefined;
            let binario = '';
            for (const b of bytes as number[]) binario += String.fromCharCode(b & 0xff);
            return `data:image/jpeg;base64,${btoa(binario)}`;
        },
        /**
         * `conteudo.anuncio` -> `AnuncioData`. Vem como string JSON (MAP<TEXT,TEXT> no
         * Cassandra); só devolve objeto de verdade, para o card nunca renderizar em
         * branco a partir de string crua, `"undefined"` ou JSON quebrado.
         */
        ToAnuncio(content: MessageContent | undefined): AnuncioData | undefined {
            const bruto = (content as AnuncioContent | undefined)?.anuncio;
            if (!bruto) return undefined;
            const parsed = ParseJsonStringOrReturnAsIs(bruto as unknown as string | object);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? parsed as AnuncioData
                : undefined;
        },
        /**
         * `conteudo.orderMessage` -> `CatalogOrderData`.
         *
         * Serve aos DOIS caminhos de propósito: o histórico gravado como
         * GENERICA e as mensagens novas já tipadas como PEDIDO_CATALOGO. Em
         * ambos o `conteudo` guarda o mesmo invólucro cru, então há um parse só.
         *
         * `totalAmount1000` vem em milésimos da unidade menor — `"69000"` é
         * R$ 69,00 — e é formatado aqui porque a bolha não deve saber disso.
         */
        ToCatalogOrderData(content: MessageContent | undefined): CatalogOrderData | undefined {
            const pedido = content as PedidoCatalogoContent | undefined;
            if (pedido?.orderMessage) return MakeMessage.Content.PedidoDoBaileys(pedido.orderMessage);
            if (ehPedidoDeCatalogo(content)) return MakeMessage.Content.PedidoDaMeta(pedido?.order);
            return undefined;
        },
        /** Transporte baileys/Evolution: `orderMessage`. Traz título e miniatura, não traz itens. */
        PedidoDoBaileys(bruto: OrderMessageCru | string): CatalogOrderData | undefined {
            const parsed = ParseJsonStringOrReturnAsIs(bruto as unknown as string | object);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
            const pedido = parsed as OrderMessageCru;

            const moeda = pedido.totalCurrencyCode || undefined;
            const itens = Number(pedido.itemCount);

            return {
                orderId         : pedido.orderId,
                titulo          : pedido.orderTitle || undefined,
                itemCount       : Number.isFinite(itens) ? itens : undefined,
                mensagem        : pedido.message || undefined,
                status          : typeof pedido.status === 'number' ? STATUS_PEDIDO_PROTO[pedido.status] : pedido.status,
                surface         : typeof pedido.surface === 'number' ? SURFACE_PEDIDO_PROTO[pedido.surface] : pedido.surface,
                moeda           : moeda,
                totalFormatado  : MakeMessage.Content.FormatarTotalPedido(LongParaNumero(pedido.totalAmount1000), moeda),
                thumbnailDataUrl: MakeMessage.Content.ToMiniaturaDataUrl(pedido.thumbnail),
            };
        },
        /**
         * Transporte OFICIAL da Meta: `order`. Espelho do de cima, e o que falta
         * é o ponto: não vem nome de produto, miniatura, total pronto, `orderId`
         * nem `status`. Vêm os ITENS, e o total é a soma deles.
         */
        PedidoDaMeta(bruto: MetaOrderCru | string | undefined): CatalogOrderData | undefined {
            if (!bruto) return undefined;
            const parsed = ParseJsonStringOrReturnAsIs(bruto as unknown as string | object);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
            const pedido = parsed as MetaOrderCru;

            const produtos = Array.isArray(pedido.product_items) ? pedido.product_items : [];
            // A moeda é declarada por item; na prática o pedido inteiro é de uma
            // moeda só, então a do primeiro item representa o total.
            const moeda = produtos.find(i => i?.currency)?.currency || undefined;

            // ⚠️ `item_price` já está na unidade da moeda — `18` é R$ 18,00.
            // NÃO existe divisão por 1000 aqui; isso é regra do outro transporte.
            const total = produtos.reduce((soma, item) => {
                const preco = Number(item?.item_price);
                const qtd = Number(item?.quantity);
                if (!Number.isFinite(preco)) return soma;
                return soma + preco * (Number.isFinite(qtd) ? qtd : 1);
            }, 0);

            const itens: CatalogOrderItem[] = produtos.map(item => ({
                sku                   : item?.product_retailer_id,
                quantidade            : Number.isFinite(Number(item?.quantity)) ? Number(item?.quantity) : undefined,
                precoUnitarioFormatado: MakeMessage.Content.FormatarValorNaMoeda(Number(item?.item_price), moeda),
            }));

            return {
                catalogId     : pedido.catalog_id,
                // `itemCount` conta PRODUTOS distintos, como o `itemCount` do
                // baileys — a quantidade de cada um vai na linha do item.
                itemCount     : produtos.length || undefined,
                mensagem      : pedido.text || undefined,
                moeda         : moeda,
                totalFormatado: produtos.length ? MakeMessage.Content.FormatarValorNaMoeda(total, moeda) : undefined,
                itens         : itens.length ? itens : undefined,
            };
        },
        /**
         * `totalAmount1000` -> texto na moeda do pedido.
         *
         * O nome do campo é literal: o valor está em milésimos da unidade menor,
         * então `"69000"` com `BRL` é R$ 69,00. Sem moeda (ou com moeda que o
         * Intl não conhece) devolve só o número formatado, nunca o valor cru em
         * milésimos — que seria lido como setenta e nove mil.
         */
        FormatarTotalPedido(total?: string | number, moeda?: string): string | undefined {
            if (total === undefined || total === null || total === '') return undefined;
            const milesimos = Number(total);
            if (!Number.isFinite(milesimos)) return undefined;
            return MakeMessage.Content.FormatarValorNaMoeda(milesimos / 1000, moeda);
        },
        /**
         * Número JÁ na unidade da moeda -> texto. Separada de
         * `FormatarTotalPedido` de propósito: o `item_price` da Cloud API oficial
         * NÃO está em milésimos, e passá-lo pelo formatador do baileys mostraria
         * R$ 0,02 no lugar de R$ 18,00.
         */
        FormatarValorNaMoeda(valor?: number, moeda?: string): string | undefined {
            if (valor === undefined || !Number.isFinite(valor)) return undefined;
            try {
                return moeda
                    ? valor.toLocaleString('pt-BR', { style: 'currency', currency: moeda })
                    : valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            } catch {
                // Moeda desconhecida pelo Intl: melhor o número puro que um throw
                // que derrubaria a montagem da mensagem inteira.
                return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
        },
        // todo !
        ToMetaTemplate(content: MessageContent, tipo: WhizMessageTipo) {
            if (tipo != "TEMPLATE_META") return undefined;

            const _parsed = content as {
                sendSuccess: 'true'|'false',
                // Só existe em tentativa recusada pela Meta (o server só grava quando falha).
                erro?: string,
                erroCodigo?: string,
                erroDetalhe?: string,
                parametersToReplace: string,
                template: string,
                tipo: 'TEMPLATE_META'
            };
            const parsed = {
                sendSuccess: _parsed.sendSuccess == 'true',
                erro: _parsed.erro,
                erroCodigo: _parsed.erroCodigo,
                erroDetalhe: _parsed.erroDetalhe,
                parametersToReplace: JSON.parse(_parsed.parametersToReplace) as { replace: null } | IMetaTemplateNamedParams | IMetaTemplatePositionalParams,
                template: JSON.parse(_parsed.template) as IMetaTemplate,
                tipo: _parsed.tipo
            };
            return parsed;
        },

        ToReplyText(dadosResposta?: WhizMessageDadosResposta): undefined | string {
            switch (dadosResposta?.tipo) {
                case "TEXTO"      : return TextContentUtil.textMessageWithoutSignature({texto: dadosResposta?.texto ?? ''}, 'TEXTO');
                case "audio"      : return  dadosResposta?.legenda || 'Áudio';
                case "AUDIO"      : return  dadosResposta?.legenda || 'Áudio';
                case "IMAGEM"     : return  dadosResposta?.legenda || 'Imagem';
                case "VIDEO"      : return  dadosResposta?.legenda || 'Vídeo';
                case "DOCUMENTO"  : return  dadosResposta?.legenda || 'Documento';
                case "CONTATO"    : return 'Contato';
                case "CONTATOS"   : return 'Contatos';
                case "FIGURINHA"  : return 'Figurinha';
                case "LOCALIZACAO": return 'Localização';
                case "LISTA"      : return  dadosResposta?.texto || 'Lista';
                case "ENQUETE"    : return 'Enquete';
                case "INTERATIVA" : return 'Botão';
                case "PIX"        : return 'Pix';
                case "EVENTO"     : return 'Evento';
                // Sem estes dois casos o `switch` caía no `default: undefined` e a
                // citação saía VAZIA — justamente no caso em que ela mais importa:
                // o contato clica num botão de template e a resposta chega sem dizer
                // a que ela responde.
                case "TEMPLATE_META"  : return dadosResposta?.texto || 'Template';
                case "LISTA_RESPOSTA" : return dadosResposta?.texto || 'Resposta';
                    default      : return undefined
            }
            return TextContentUtil.textMessageWithoutSignature({texto: dadosResposta?.texto ?? ''}, dadosResposta?.tipo ?? 'TEXTO') || dadosResposta?.legenda || (dadosResposta?.tipo == 'AUDIO' ? 'Áudio' : '')
        },
        ToLegenda(content: MessageContent): undefined | string {
            const legenda = (content as ReceivedContentBase)?.legenda;
            if (legenda) return legenda;

            // Documentos com legenda chegam sem `legenda`: o texto vem no payload cru
            // `documentWithCaptionMessage` (string JSON da Evolution).
            return MakeMessage.Content.ToDocumentCaption(content);
        },

        /**
         * Extrai a legenda do payload cru `documentWithCaptionMessage`.
         * Aceita as variações de aninhamento da Evolution:
         * `{message:{documentMessage:{caption}}}`, `{documentMessage:{caption}}` e `{caption}`.
         */
        ToDocumentCaption(content: MessageContent): undefined | string {
            const raw = (content as { documentWithCaptionMessage?: string|object })?.documentWithCaptionMessage;
            if (!raw) return undefined;

            const parsed = ParseJsonStringOrReturnAsIs(raw) as {
                caption?: string,
                documentMessage?: { caption?: string },
                message?: { caption?: string, documentMessage?: { caption?: string } },
            } | undefined | null | string | number;

            if (!parsed || typeof parsed !== 'object') return undefined;

            return parsed.message?.documentMessage?.caption
                ?? parsed.message?.caption
                ?? parsed.documentMessage?.caption
                ?? parsed.caption
                ?? undefined;
        },

        ToEventData(content: MessageContent, tipo: WhizMessageTipo): EventData | undefined {
            if (tipo !== 'EVENTO') return undefined;

            const outer = ParseJsonStringOrReturnAsIs(content) as any;
            // O evento pode chegar plano (`{nome, dataHoraInicio, ...}`) ou dentro do
            // invólucro cru do WhatsApp (`{eventMessage: '<json>'}`), que é o formato
            // gravado quando a mensagem cai no fallback GENERICA.
            const parsed = (typeof outer === 'object' && outer !== null && outer.eventMessage)
                ? ParseJsonStringOrReturnAsIs(outer.eventMessage) as any
                : outer;

            if (typeof parsed !== 'object' || parsed === null) return { name: 'Convite para Evento' }; // Fallback

            // `location` do protocolo é objeto (`{degreesLatitude, degreesLongitude, name}`)
            // e o `name` costuma carregar um link de sala, não um endereço.
            const rawLocation = parsed.localizacao ?? parsed.location ?? parsed.endereco ?? parsed.local;
            const locationName = typeof rawLocation === 'string'
                ? rawLocation
                : (rawLocation as { name?: string }|undefined)?.name;
            const locationIsLink = !!locationName && /^https?:\/\//i.test(locationName);

            return {
                name: parsed.nome || parsed.name || parsed.titulo || 'Convite para Evento',
                description: parsed.descricao || parsed.description,
                startTime: MakeMessage.Content.ToEventTime(parsed.dataHoraInicio ?? parsed.startTime ?? parsed.hora ?? parsed.data),
                endTime: MakeMessage.Content.ToEventTime(parsed.dataHoraFim ?? parsed.endTime),
                location: locationIsLink ? undefined : locationName,
                joinLink: parsed.linkChamada || parsed.joinLink || parsed.link || (locationIsLink ? locationName : undefined),
                isCanceled: parsed.cancelado || parsed.isCanceled || false,
                guestCount: parsed.quantidadeConvidados || parsed.guestCount || parsed.presencas || undefined,
            };
        },

        /**
         * Normaliza a hora do evento para exibição. O protocolo manda epoch em
         * SEGUNDOS — como string (`'1787882400'`), número, ou Long protobuf
         * (`{low, high}`). Qualquer outra coisa volta como veio.
         */
        ToEventTime(raw: unknown): string|undefined {
            if (raw === undefined || raw === null || raw === '') return undefined;

            const asLong = raw as { low?: number, high?: number };
            const seconds = typeof asLong?.low === 'number'
                ? asLong.low + (asLong.high ?? 0) * 4294967296
                : (typeof raw === 'number' || /^\d+$/.test(String(raw)) ? Number(raw) : NaN);

            if (!Number.isFinite(seconds) || seconds <= 0) return typeof raw === 'string' ? raw : undefined;

            // Epoch em segundos: se vier em milissegundos (13 dígitos), não multiplica.
            const ms = String(Math.trunc(seconds)).length > 12 ? seconds : seconds * 1000;
            const date = new Date(ms);
            if (Number.isNaN(date.getTime())) return typeof raw === 'string' ? raw : undefined;

            return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
        },
        ToLocationData(content: MessageContent, tipo: WhizMessageTipo): undefined | { lat: number; lng: number; address?: string } {
            if (tipo === 'LOCALIZACAO') {
                const c = content as LocalizationContent;
                return {
                    lat: Number(c.latitude),
                    lng: Number(c.longitude),
                    // @ts-expect-error yup
                    address: c.endereco || c.name || c.address // eslint-disable-line
                };
            }
            return undefined;
        },

        ToListData(content: MessageContent, tipo : WhizMessageTipo): undefined | ListData { switch (tipo) {
            case "LISTA": {
                // Lista ENVIADA pelo Whiz tem outra forma (`{titulo, lista}`); sem
                // este ramo a bolha saía sem opções e sem o rótulo do botão.
                const enviada = lerListaEnviada(content);
                if (enviada) {
                    return {
                        listButtonText: enviada.rotuloBotao,
                        listHeader: [(content as { titulo?: string }).titulo, enviada.titulo].filter(t => t?.trim()).join('\n\n'),
                        listOptions: (enviada.opcoes ?? []).map(o => ({ id: String(o.id), text: o.titulo, description: o.descricao }) as ListOption),
                    };
                }
                const lcontent = content as ListContent;
                const temp = ParseJsonStringOrReturnAsIs(lcontent.secoes) as Array<ListContentSecao>;
                const secoes = Array.isArray(temp) ? temp : [];
                return {
                    listButtonText: lcontent.textoBotao,
                    // O WhatsApp renderiza cabeçalho (titulo) e corpo (descricao); exibir só o
                    // titulo esconde a mensagem quando ela vem no corpo.
                    listHeader: [lcontent.titulo, lcontent.descricao].filter(t => t?.trim()).join('\n\n'),
                    listOptions: secoes.flatMap(s => s.items?.map(i => ({id: String(i.id), text: i.titulo, description: i.descricao}) as ListOption) ?? []),
                }; 
            }
            case 'PIX': {
                const rcontent = (content as ReceivedPixContent);
                if (rcontent.chave) {
                    // Cobrança `payment_request` não traz titular: sem a linha "Nome:" o
                    // card esconde o campo. O texto da bolha (descrição/valor) vai
                    // depois da chave, separado por linha em branco.
                    const linhas = [
                        '**Chave PIX**',
                        '',
                        ...(rcontent.titular?.trim() ? [`Nome: ${rcontent.titular}`] : []),
                        `Chave: ${rcontent.chave}`,
                        ...(rcontent.corpo?.trim() ? ['', rcontent.corpo.trim()] : []),
                    ];
                    return {
                        listHeader: linhas.join('\n'),
                        listButtonText: 'Copiar Chave Pix',
                    };
                } else {
                    // todo: todo
                    const scontent = (content as SentPixContent);
                    console.log(scontent);
                    return undefined;
                }
            }
            case 'INTERATIVA': {
                const ccontent = (content as InteractiveContent);
                if (!ccontent) return undefined;
                const temp = ParseJsonStringOrReturnAsIs(ccontent.botoes) as Array<{tipo: string, texto: string, codigoCopia?: string, url?: string, telefone?: string, idResposta?: string}>;
                const botoes = Array.isArray(temp) ? temp : [];
                // OTP de código único: mantém o card dedicado "Senha de Acesso" (já copia ao clicar).
                if (botoes.length === 1 && !!botoes[0]?.codigoCopia) {
                    return {
                        listHeader: ccontent.corpo + `Código:*${botoes[0].codigoCopia}*`,
                        listButtonText: botoes[0].texto
                    }
                }
                // Demais botões: chips inline, cada um com sua ação (copiar código / abrir link / ligar / só exibir).
                return {
                    listHeader: ccontent.rodape ? `${ccontent.corpo}\n${ccontent.rodape}` : ccontent.corpo,
                    listInline: true,
                    listOptions: botoes.map((b, i) => ({
                        id: String(b.idResposta ?? b.codigoCopia ?? i),
                        text: b.texto,
                        actionType: b.tipo,
                        copyCode: b.codigoCopia,
                        url: b.url,
                        phone: b.telefone,
                    }) as ListOption),
                }
            }
            default: return undefined;
        } },

        ToPollData(content: MessageContent, tipo: WhizMessageTipo): undefined|PollData { switch (tipo) {
            case 'ENQUETE': {
                const rcontent = (content as ReceivedPollContent);
                if (rcontent.nome) {
                    const parsedOptions = ParseJsonStringOrReturnAsIs(rcontent.opcoes)
                    const arrayOfParsed: unknown[] = Array.isArray(parsedOptions) ? parsedOptions : [parsedOptions];
                    // Contagem por índice, gravada pelo servidor a cada voto. Mesma
                    // ordem de `opcoes` — o WhatsApp identifica a opção votada pelo
                    // SHA-256 do NOME, e é o servidor que traduz aquilo para índice.
                    // Vem como STRING JSON porque `conteudo` é MAP<TEXT,TEXT> no
                    // Cassandra e nada é desserializado na volta.
                    const parsedVotes = ParseJsonStringOrReturnAsIs(rcontent.votosContagem)
                    const votesByIndex: number[] = Array.isArray(parsedVotes) ? parsedVotes as number[] : [];
                    // Votantes DISTINTOS, não a soma: em múltipla escolha uma pessoa
                    // soma em várias opções e continua sendo um votante.
                    // Votantes ATIVOS. Quem retira o voto continua no mapa como lápide
                    // (`opcoes: []`) — o `ts` dela é a marca d'água de ordem do servidor —
                    // e lápide não é gente votando. Contar as chaves faria a enquete dizer
                    // "1 voto" com todas as barras zeradas depois de um reload; o evento
                    // ao vivo (`MENSAGEM.ENQUETE_VOTO`) já manda o número certo, então as
                    // duas fontes divergiam.
                    const parsedVoters = ParseJsonStringOrReturnAsIs(rcontent.votos)
                    // Recolhido para chaves canônicas ANTES de qualquer agregação: linha
                    // gravada quando o servidor usava o número cru pode ter o mesmo
                    // humano sob a chave legada E sob a canônica, e o servidor só cura a
                    // linha no próximo voto aceito. Sem recolher aqui, `totalVoters`
                    // soma 2 e o popover lista a mesma pessoa duas vezes.
                    const votosPorVotante = canonizarVotosDeEnquete(
                        parsedVoters && typeof parsedVoters === 'object' && !Array.isArray(parsedVoters)
                            ? parsedVoters as Record<string, {opcoes?: unknown[], ts?: unknown}>
                            : {},
                    );
                    // Um par por votante ATIVO. A lápide fica de fora aqui e em
                    // `votersByIndex`: ela não tem opção para aparecer sob nenhuma barra.
                    const votantesAtivos = Object.entries(votosPorVotante)
                        .filter(([, v]) => Array.isArray(v?.opcoes) && v.opcoes.length > 0);
                    const totalVoters = votantesAtivos.length;
                    // Quem votou, por índice de opção — é o que o card mostra no popover
                    // de cada barra. `opcoes` guarda ÍNDICE, não texto: o WhatsApp manda
                    // o SHA-256 do nome da opção e é o servidor que traduz para índice,
                    // na mesma ordem de `opcoes`.
                    const votersByIndex = new Map<number, string[]>();
                    for (const [votante, voto] of votantesAtivos) {
                        for (const cru of (voto.opcoes as unknown[])) {
                            const indice = Number(cru);
                            // Índice inválido ou fora de faixa só aparece se a enquete
                            // encolher depois de votada; o servidor ignora do mesmo jeito
                            // ao contar, então o popover não pode inventar uma posição.
                            if (!Number.isInteger(indice) || indice < 0 || indice >= arrayOfParsed.length) continue;
                            const lista = votersByIndex.get(indice);
                            if (!lista) votersByIndex.set(indice, [votante]);
                            // Múltipla escolha reenvia a seleção completa; um votante
                            // repetido no mesmo índice contaria duas vezes na lista.
                            else if (!lista.includes(votante)) lista.push(votante);
                        }
                    }
                    return {
                        question: rcontent.nome,
                        // `conteudo` é MAP<TEXT,TEXT> no Cassandra: o servidor grava
                        // `String(false)`, então isto volta como a STRING "false" — que é
                        // truthy. O tipo `boolean` de `PollData` mente sobre o que chega.
                        allowMultiple: rcontent.permiteMultiplasRespostas === true
                            || String(rcontent.permiteMultiplasRespostas) === 'true',
                        totalVoters,
                        options: arrayOfParsed.map((o, index) => ({
                            id: getRandomInt(0, 1_048_576),
                            text: (o as {opcao: string})?.opcao,
                            votes: Number(votesByIndex[index] ?? 0),
                            voters: votersByIndex.get(index) ?? [],
                            isChecked: false
                        } as PollOption)),
                    }
                }

                // Enquete legada: o servidor nunca traduziu esta variante, então o
                // `conteudo` guarda a forma CRUA do protocolo (`name`/`options[].optionName`
                // em vez de `nome`/`opcoes[].opcao`). Sem votos — eles nunca foram
                // apurados para estas mensagens.
                const legada = encontrarEnqueteLegada(content);
                if (legada?.name) {
                    const opcoes = Array.isArray(legada.options) ? legada.options : [];
                    return {
                        question: legada.name,
                        allowMultiple: legadaPermiteMultiplas(legada),
                        totalVoters: 0,
                        options: opcoes.map(o => ({
                            id: getRandomInt(0, 1_048_576),
                            text: (o as {optionName?: string})?.optionName ?? '',
                            votes: 0,
                            voters: [],
                            isChecked: false
                        } as PollOption)),
                    }
                }

                return undefined;
            }
            default: return undefined
        }  },

        ToContactData(content: MessageContent, tipo: WhizMessageTipo): undefined|ContactData[] {
            const fixPhone = (p: string) => p?.replace(/^(55\d{2})([6-9]\d{7})$/, "$19$2");

            switch (tipo) {
                case "CONTATO": {
                    const scontent = content as SingleContactContent;
                    if (scontent.nome) {
                        return [{
                            avatar  : scontent.photo == 'null' || !scontent.photo ? undefined : scontent.photo,
                            initials: extractInitials(scontent.nome),
                            name    : scontent.nome,
                            phone   : fixPhone(scontent.telefone),
                        }]
                    } else return undefined
                }
                case "CONTATOS": {
                    const mcontent = content as MultipleContactsContent;
                    const contatos = ParseJsonStringOrReturnAsIs(mcontent.contatos) as {photo?: string|null, nome: string, telefone: string}[];
                    if (contatos.length > 0) {
                        return contatos.map(c => ({
                            avatar  : (c.photo == 'null' || !c.photo) ? undefined : c.photo,
                            initials: extractInitials(c.nome),
                            name    : c.nome,
                            phone   : fixPhone(c.telefone),
                        }))
                    } else return undefined
                }
                default: return undefined;
            }
        },

        ToFileData(content: MessageContent, tipo: WhizMessageTipo, sentByMe: boolean, ): undefined|FileData {
            const getMime = (tipo: WhizMessageTipo) => {
                    switch(tipo) {
                    case "IMAGEM": return 'image/*';
                    case "VIDEO": return 'video/*';
                    case "audio": return 'audio/*';
                    case "AUDIO": return 'audio/*';
                    case "FIGURINHA": return 'image/*';
                    case "DOCUMENTO": return 'application/*';
                    case "TEXTO":
                    case "CONTATO":
                    case "CONTATOS":
                    case "LOTTIE":
                    case "LOCALIZACAO":
                    case "REACAO":
                    case "LISTA":
                    case "LISTA_RESPOSTA":
                    case "ENQUETE":
                    case "INTERATIVA":
                    case "PIX":
                    case "EVENTO":
                    case "GENERICA":
                    return undefined;
                }
            };
            // que deus perdoe quem inventou o operador ternário
            const getName = (sentByMe: boolean, content: MessageContent) => {
              //if ((content as ReceivedContentBase).nome_documento) return (content as ReceivedContentBase).nome_documento;
              if (sentByMe)
                return (content as SentContentBase).codigo || (content as ReceivedContentBase).url?.split('/').reverse().slice(0,1).join('')

              return (content as ReceivedContentBase).url?.split('/').reverse().slice(0,1).join('');
            }
            const getUrl = (sentByMe: boolean, tipo: WhizMessageTipo, content: MessageContent) => {
                if ((content as ReceivedContentBase).url) return (content as ReceivedContentBase).url;
                // if (!sentByMe) return (content as ReceivedContentBase).url;
                switch(tipo) {
                    // case "FIGURINHA": return (content as SentStickerContent).;
                    case "IMAGEM"     : return (content as SentImageContent).imagem;
                    case "VIDEO"      : return (content as SentVideoContent).video;
                    case "AUDIO"      : return (content as SentAudioContent).audio;
                    case "DOCUMENTO"  : return (content as SentDocumentContent).document || (content as ReceivedDocumentContent).url;
                        default      : return '';
                }
            };

            const mime = getMime(tipo);
            if (!mime) return undefined;
            const fileData: FileData = {
                caption: MakeMessage.Content.ToLegenda(content),
                mimeType: mime,
                name: getName(sentByMe, content),
                size: '',
                url: getUrl(sentByMe, tipo, content),
            };
            return fileData;
        },

        ToLocalizationData(content: MessageContent, tipo: WhizMessageTipo,): LocalizationData | undefined {
            switch (tipo) {
                case "LOCALIZACAO": {
                    const localizationContent = content as LocalizationContent;
                    return {
                        latitude : localizationContent.latitude,
                        longitude: localizationContent.longitude,
                        endereco : localizationContent.endereco,
                        nome     : localizationContent.nome,
                        url      : localizationContent.url,
                    }
                }
                default: return undefined;
            }
        },

        ToGroupInviteData(content: MessageContent, tipo: WhizMessageTipo): GroupInviteData | undefined {
            if (tipo !== 'CONVITE_GRUPO') return undefined;
            const c = content as GroupInviteContent;
            return {
                nomeGrupo    : c.nomeGrupo,
                codigoConvite: c.codigoConvite,
                jidGrupo     : c.jidGrupo,
                caption      : c.caption,
            };
        },
    }

    /**
     * A bolha oferece "Reenviar"?
     *
     * Só para o que o reenvio REALMENTE atende: mensagem nossa, de tipo que
     * `retryFailedMessage` sabe remontar, e com a situação `FALHOU` — a que o Whiz grava
     * quando o envio falhou de fato. Ficam de fora: template Meta recusado, lista/PIX,
     * figurinha e contato (sem caminho de reenvio, o clique não mandaria nada) e o
     * `failed` cru do webhook da Meta, que o servidor recusa com 409 por não ser `FALHOU`.
     */
    static PodeReenviar(wMsg: { enviadaPorMim: boolean; situacao: WhizMessageSituacao }, tipo: string): boolean {
        const TIPOS_REENVIAVEIS = ['TEXTO', 'IMAGEM', 'VIDEO', 'DOCUMENTO'];
        return !!wMsg.enviadaPorMim
            && String(wMsg.situacao ?? '').toUpperCase() === 'FALHOU'
            && TIPOS_REENVIAVEIS.includes(String(tipo ?? '').toUpperCase());
    }

    static ReadStatus = {
        FromWhizSituacao(inSituacao: WhizMessageSituacao): ChatReadStatus {
            const situacao = inSituacao.toUpperCase();
            // `FALHOU`: a plataforma RECUSOU o envio (ex.: template Meta sem método de
            // pagamento). Sem este ramo a recusa cairia no `pending` genérico e a bolha
            // ficaria com o relógio de "enviando" para sempre.
            return ['FALHOU','FAILED'].includes(situacao) ? 'error'
                : ['ENVIADO','ENVIANDO','SENT'].includes(situacao) ? 'sent'
                : ['RECEIVED','ENTREGUE','DELIVERED'].includes(situacao) ? 'delivered'
                : ['READ','LIDA'].includes(situacao) ? 'read' : 'pending'
        }
    }

    static IsSent = {
        FromEnviadaPorMim(envPorMim: string): boolean {
            return [true, 'true', 1, '1', 'TRUE', 'sim', 'SIM'].includes(envPorMim)
        }
    }

    static Tipo = {
        /**
         * Resolve o tipo efetivo da mensagem.
         * Quando o servidor classifica como GENERICA mas o conteúdo declara o próprio tipo
         * (ex.: `conteudo.tipo == 'DOCUMENTO'` com `url`/`nome_documento`), usamos o tipo do
         * conteúdo para que a mensagem seja renderizada como documento em vez de genérica.
         */
        Resolve(inType: WhizMessageTipo, content?: MessageContent): WhizMessageTipo {
            if (inType?.toUpperCase() !== 'GENERICA') return inType;

            const contentTipo = (content as { tipo?: string }|undefined)?.tipo?.toUpperCase();
            const hasFile = !!(content as ReceivedContentBase|undefined)?.url;

            if (contentTipo === 'DOCUMENTO' && hasFile) return 'DOCUMENTO';

            // Vídeo-recado (vídeo redondo, `ptvMessage`): o servidor baixa a mídia
            // (a `url` está no conteúdo), mas o transformer não tem o tipo e grava
            // GENERICA. Renderiza como vídeo comum; recupera também o histórico.
            if (ehVideoRecado(content)) return 'VIDEO';

            // Evento de agenda do WhatsApp (`eventMessage`): o servidor ainda não
            // mapeia esse tipo e grava como GENERICA, mas o conteúdo traz o
            // invólucro `eventMessage` (string JSON ou objeto). Já temos card de
            // evento no ChatMessage, então basta resolver o tipo aqui.
            if ((content as { eventMessage?: unknown }|undefined)?.eventMessage) return 'EVENTO';

            // Enquete gravada ANTES do fix de variantes. O WhatsApp tem seis campos
            // de criação de enquete e o servidor só entendia `pollCreationMessageV3`,
            // então tudo que veio nas outras cinco foi persistido como GENERICA — e
            // GENERICA aqui vira `list-answer`, ou seja, bolha vazia. O conteúdo cru
            // sobreviveu no `conteudo` (aquele campo nunca foi validado nem filtrado),
            // então dá para resolver o tipo na leitura e recuperar o histórico.
            // Mensagem NOVA já chega como ENQUETE e nem passa por aqui.
            if (encontrarEnqueteLegada(content)) return 'ENQUETE';

            // Pedido do catálogo (`orderMessage`): mesma história do evento de
            // agenda. `orderMessage` não existe no `MessageUpsertType` do
            // servidor, então TODO pedido de catálogo foi gravado como GENERICA
            // — e GENERICA vira `list-answer`, bolha vazia, que o
            // `ChatConversation` ainda por cima descarta. O invólucro cru
            // sobreviveu no `conteudo`, então o histórico inteiro se recupera
            // resolvendo o tipo aqui. Mensagem nova já chega PEDIDO_CATALOGO e
            // nem entra neste bloco.
            // Os DOIS transportes: `orderMessage` é o do baileys/Evolution,
            // `order` é o da Cloud API oficial. `order` é um nome de chave
            // genérico demais para servir de marca sozinho, então exige-se a
            // assinatura do pedido (`product_items`/`catalog_id`).
            if (ehPedidoDeCatalogo(content)) return 'PEDIDO_CATALOGO';

            // Lista de opções enviada pelo Whiz (`conteudo.lista`): o webhook de
            // status da Meta regravava a linha como GENERICA e ela sumia da
            // conversa. O servidor foi corrigido; isto recupera o histórico.
            if (ehListaEnviadaGravadaComoGenerica(content)) return 'LISTA';

            return inType;
        }
    }

    static Type = {
        FromWhizMsgTipo(inType: WhizMessageTipo): ChatMessageType {
            inType = inType.toUpperCase() as WhizMessageTipo;
            // PEDIDO_CATALOGO vem ANTES das linhas de GENERICA de propósito: a
            // cadeia é primeira-que-casa, e `Tipo.Resolve` promove o histórico
            // GENERICA para cá.
            return ['PEDIDO_CATALOGO'].includes(inType) ? 'catalog-order' :
                [''].includes(inType) ? 'sticker' :
                ['INTERATIVA','LISTA','PIX'].includes(inType) ? 'list' :
                ['GENERICA','LISTA_RESPOSTA'].includes(inType) ? 'list-answer' :
                ['AUDIO'].includes(inType) ? 'audio' :
                ['?????'].includes(inType) ? 'system' :
                ['ENQUETE'].includes(inType) ? 'poll' :
                ['FIGURINHA'].includes(inType) ? 'sticker' :
                ['IMAGEM','LOTTIE'].includes(inType) ? 'imagem' :
                ['VIDEO'].includes(inType) ? 'video' :
                ['DOCUMENTO'].includes(inType) ? 'file' :
                ['CONTATO'].includes(inType) ? 'contact' :
                ['CONTATOS'].includes(inType) ? 'multi_contact' :
                ['LOCALIZACAO'].includes(inType) ? 'location' :
                ['EVENTO'].includes(inType) ? 'event' :
                ['MULTI_CONTACT', 'MULTICONTACT'].includes(inType) ? 'multi_contact' :
                ['VISUALIZACAO_UNICA'].includes(inType) ? 'view-once' :
                ['NAO_SUPORTADA'].includes(inType) ? 'unsupported' :
                ['GENERICA','TEXTO'].includes(inType) ? 'text' :
                ['TEMPLATE_META'].includes(inType) ? 'template_meta' :
                ['CONVITE_GRUPO'].includes(inType) ? 'group-invite' : 'text';
        }
    }

    static Subtype = {
        FromWhizMsgTipo(inType: WhizMessageTipo): 'sticker'|undefined {
            return ['FIGURINHA'].includes(inType) ? 'sticker' : undefined
        }
    }

    static Async = {
        async FetchLastMessageContentUntilCanParse(chatObj: Chat, atendentes: ResponseUsuario[] = []) {
            let responseMessage: ResponseMessage;

            let i = 0;
            do {
                if (chatObj.isGroup)
                    responseMessage = await new MensagensService().getMensagensByGrupo(chatObj.id as IdGrupo, undefined, 1 + i);
                else
                    responseMessage = await new MensagensService().getMensagensByAtendimento(chatObj.id as IdAtendimento, 1, 1 + i);
                const actualLastMessage = responseMessage.items.at(-1)

                if (!actualLastMessage) return;


                if (actualLastMessage.tipo == "REACAO") {
                    chatObj.lastMessage.text   = `${actualLastMessage.enviadaPorMim ? atendentes.at(0)?.nome || '' : chatObj.name} reagiu com ${(actualLastMessage.conteudo as ReceivedReactionContent).reacao}`;
                    chatObj.lastMessage.type   = 'text';
                    chatObj.lastMessage.sender = null;
                    chatObj.lastMessage.ogType = 'REACAO';
                    break;
                } else if (actualLastMessage.tipo == "GENERICA" && (actualLastMessage.conteudo as { albumMessage?: string })?.albumMessage ) {
                    i++;
                    continue;
                } else {
                    chatObj.lastMessage.text     = getDisplayTextForMessageContent(actualLastMessage.conteudo, actualLastMessage.tipo) || '';
                    chatObj.lastMessage.sender   = TextContentUtil.extractSignature(actualLastMessage.conteudo) || null;
                    chatObj.lastMessage.isFromMe = actualLastMessage.enviadaPorMim;
                    chatObj.lastMessage.type     = MakeMessage.Type.FromWhizMsgTipo(actualLastMessage.tipo);
                    chatObj.lastMessage.ogType   = actualLastMessage.tipo;
                    break;
                }
            } while (i < 10);
        },
        async FetchLastReactionContent(chatObj: Chat, atendentes: ResponseUsuario[] = [], ) {
            let responseMessage: ResponseMessage;

            if (chatObj.isGroup)
                responseMessage = await new MensagensService().getMensagensByGrupo(chatObj.id as IdGrupo, undefined, 1);
            else
                responseMessage = await new MensagensService().getMensagensByAtendimento(chatObj.id as IdAtendimento, 1, 1);
            const actualLastMessage = responseMessage.items.at(0)

            if (actualLastMessage && actualLastMessage.tipo == "REACAO") {
                chatObj.lastMessage.text = `${actualLastMessage.enviadaPorMim ? atendentes.at(0)?.nome || '' : chatObj.name} reagiu com ${(actualLastMessage.conteudo as ReceivedReactionContent).reacao}`;
                chatObj.lastMessage.sender = null;
            }
        },
    }
}

/**
 * Rótulo de uma publicação do Instagram compartilhada no direct, ou `undefined`
 * quando o conteúdo não é um compartilhamento.
 *
 * O conteúdo chega como `compartilhamento` — objeto no evento ao vivo, string
 * JSON quando vem do Cassandra (`conteudo` é MAP<TEXT,TEXT>). Aqui só interessa
 * saber se existe e qual a origem, então a string é lida sem parse completo.
 */
export function textoDeCompartilhamento(conteudo: MessageContent | undefined): string | undefined {
    const bruto = (conteudo as CompartilhamentoContent | undefined)?.compartilhamento;
    if (!bruto) return undefined;
    const origem = typeof bruto === 'string'
        ? (bruto.includes('"ig_reel"') ? 'ig_reel' : undefined)
        : bruto.origem;
    return origem === 'ig_reel' ? '🎬 Reel do Instagram' : '📷 Publicação do Instagram';
}

/** Rótulo do selo que fica ACIMA da mídia, onde há largura de sobra. */
export function rotuloDeCompartilhamento(origem?: string): string {
    return origem === 'ig_reel' ? 'Reel do Instagram' : 'Publicação do Instagram';
}

/**
 * Rótulo do CARTÃO. Curto de propósito: o cartão tem miniatura de 64px ao lado e
 * sobra pouca largura, e em caixa alta "REEL COMPARTILHADO" quebrava em duas
 * linhas (depois, com `nowrap`, virava "REEL DO INSTAGR…"). O ícone do
 * Instagram ao lado já diz de qual rede é.
 */
export function rotuloCurtoDeCompartilhamento(origem?: string): string {
    return origem === 'ig_reel' ? 'Reel' : 'Publicação';
}

export class TextContentUtil {
    static textMessageWithoutSignature(conteudo: MessageContent, tipo: WhizMessageTipo, removerAssinatura?: boolean) {
        switch (tipo) {
            case "LISTA_RESPOSTA": return (conteudo as ListAnswerContent).texto;
            case "EVENTO"        : return '📆 Evento';
            // GENERICA é o balde do que o servidor não soube tipar — mas nem tudo
            // que cai aqui é vazio. Publicação do Instagram compartilhada chega
            // como GENERICA com `compartilhamento` no conteúdo, e devolver
            // '❔ Genérica' aqui a fazia SUMIR da conversa: `ChatConversation`
            // descarta mensagem cujo texto contenha essa frase.
            case "GENERICA"      : return textoDeCompartilhamento(conteudo) ?? '❔ Genérica'
            // Este texto é também o que salva a mensagem do filtro do
            // `ChatConversation`, que descarta tudo que contenha '❔ Genérica'.
            case "PEDIDO_CATALOGO": return '🛍️ Pedido do catálogo'
            case "VISUALIZACAO_UNICA": return '🔒 Mensagem de visualização única — abra o WhatsApp no celular para visualizar'
            case "NAO_SUPORTADA" : return '⚠️ Mensagem de tipo não suportado pelo WhatsApp'
            case "INTERATIVA"    : return '❓ Interativa'
            case "LOCALIZACAO"   : return '🗺️ Localização'
            case "CONVITE_GRUPO" : return '👥 Convite para grupo'
            default: {
                const mensagem = (conteudo as ReceivedTextContent)?.texto;
                if (!mensagem || mensagem.length <= 1) return mensagem;
                if (removerAssinatura && /^\*([^:\n]+):\*\n/.test(mensagem))
                    return TextContentUtil.extractNonSignature(mensagem);
                return mensagem;
            }
        }
    }

    static extractNonSignature(text: string|undefined) {
        if (!text) return text;
        if (/^\*([^:\n]+):\*\n/.test(text)) return text?.replace(/^\*([^:\n]+):\*\n/, '');
        // if (/^\*([^:\n]+):\*$/.test(text)) return text?.replace(/^\*([^:\n]+):\*$/, '');
        return text;
    }

    static extractSignature(conteudo: MessageContent) {
        const mensagem = (conteudo as ReceivedTextContent)?.texto || (conteudo as ReceivedTextContent)?.texto;
        if (!mensagem) return '';
        if (/^\*([^:\n]+):\*\n/.test(mensagem)) return /^\*([^:\n]+):\*\n/.exec(mensagem)?.at(1);
        // if (/^\*([^:\n]+):\*$/.test(mensagem)) return /^\*([^:\n]+):\*$/.exec(mensagem)?.at(1);
        return ''
    }
}




/**
 * Chave de quem reagiu, no Map `reactions` da mensagem.
 *
 * O WhatsApp guarda UMA reação por remetente por mensagem, e todos os operadores de um
 * inbox mandam pelo mesmo número: a reação nova de qualquer operador substitui a
 * anterior no celular do contato. Por isso a reação nossa usa uma chave única, e não o
 * id do operador — chavear por operador mostrava 👍 de um e ❤️ de outro ao mesmo tempo,
 * quando do outro lado só existe o ❤️.
 *
 * Reação recebida é chaveada pelo contato que reagiu: `contato.codigo` em grupo,
 * `idContato` em conversa 1:1 (lá a linha não carrega o snapshot `contato`, e a reação
 * do cliente colidia com a nossa na chave vazia).
 */
export const CHAVE_REACAO_PROPRIA = 'eu';

export function chaveDoReator(mensagem: WhizMessageAtendimento | WhizMessageGrupo): string {
    if (mensagem.enviadaPorMim) return CHAVE_REACAO_PROPRIA;
    return (mensagem as WhizMessageGrupo).contato?.codigo
        || (mensagem as { idContato?: string | null }).idContato
        || '';
}

export function WhizMessagesToMessagesWithReaction(whizMessages: ResponseMessage, sortByDataCriacao: boolean = true) {
    const { whizReactions, whizNonReactions, whizLists, whizListAnswers } = whizMessages.items.reduce((obj, m) => {
        const defaultHandler = (m: WhizMessageAtendimento) => obj.whizNonReactions.push(m)
        // Unsend de Instagram/Messenger: a mensagem não é renderizada de forma
        // alguma. O servidor já a filtra; isto cobre linhas gravadas antes da
        // feature e leituras que escapem daquele filtro.
        if (mensagemOcultaPorExclusao(m)) return obj;
        switch (m.tipo) {
            case 'LISTA'         : {
                const listData = MakeMessage.Content.ToListData(m.conteudo, m.tipo);
                if (!listData) {
                    console.error(m);
                    throw new Error(`[chatUtils] [WhizMessagesToMessagesWithReaction] message was of type list but had no list content`)
                }
                obj.whizLists.push({...listData, ...{answerIdToTextMap: new Map(listData.listOptions?.map(o => [o.id, o.text]))}});
                defaultHandler(m);
            }   break;
            case 'LISTA_RESPOSTA':
                obj.whizListAnswers.push(m);
                defaultHandler(m);
                break;
            case 'REACAO'        :
                obj.whizReactions.push(m);
                break;
            default              :
                defaultHandler(m);
                break;
        }
        return obj;
    }, { whizReactions: [] as (WhizMessageAtendimento|WhizMessageGrupo)[], whizNonReactions: [] as WhizMessageAtendimento[], whizLists: [] as (ListData & { answerIdToTextMap: Map<string, string> })[], whizListAnswers: [] as WhizMessageAtendimento[] })

    // const whizAnswerIdToTextMap = new Map(whizLists.flatMap(l => Array.from(l.answerIdToTextMap.entries())))
    // whizListAnswers.forEach(answerMsg => {
    //   console.log(answerMsg);
    //     answerMsg.tipo = 'TEXTO';
    //     const resposta = whizAnswerIdToTextMap.get(`${(answerMsg.conteudo as ListAnswerContent).idResposta}`) ?? '📃 Resposta à lista'
    //     answerMsg.conteudo = { tipo: 'TEXTO', texto: resposta };
    // })

    const messageArr = whizNonReactions.map(wMsg => MakeMessage.FromWhizMessage(wMsg));

    console.log('pneumoultramicroscopicosilicovulcanoconiotico', whizReactions);

    const messageMapByIdExterno = new Map(messageArr.map(m => [m.idExterno, m]));
    whizReactions.reverse().forEach(reactionMsg => {
        const reacaoContent  = reactionMsg.conteudo as ReceivedReactionContent;
        const reactedMessage = messageMapByIdExterno.get(reacaoContent.idMensagem);

        if (!reactedMessage) return;
        const idOrCodigoReactor = chaveDoReator(reactionMsg);
        const reactionOrUnreaction = reacaoContent.reacao;
        if (!reactionOrUnreaction)
            reactedMessage.reactions?.delete(idOrCodigoReactor);
        else
            reactedMessage.reactions?.set(idOrCodigoReactor, reactionOrUnreaction)
        // console.log(idOrCodigoReactor, 'reacted with', reactionOrUnreaction, 'on message', reactedMessage)
        // console.log('wasUnreaction?', !reactionOrUnreaction);
    });

    const sorted = sortByDataCriacao ? messageArr.sort((a,b) => a.time < b.time ? 1 : -1 ) : messageArr;
    return sorted;
}

export function GetReplyIconFromType(msgType?: ChatMessageType, fileData?: FileData) {
    if (fileData?.mimeType.includes('image')) msgType = 'imagem';
    switch (msgType) {
        case 'imagem'       : return 'ri-image-line me-1';
        case 'file'         : return 'ri-file-text-line me-1';
        case 'video'        : return 'ri-video-line me-1';
        case 'audio'        : return 'ri-music-2-line me-1';
        case 'contact'      : return 'ri-user-line me-1';
        case 'multi_contact': return 'ri-user-line me-1';
        case 'location'     : return 'ri-map-pin-2-line me-1';
        case 'poll'         : return 'ri-chat-poll-line me-1';
        case 'sticker'      : return 'ri-emoji-sticker-line me-1';
        case 'list'         : return 'ri-list-check me-1';
        case 'list-answer'  : return 'ri-list-check-3 me-1';
        case 'event'        : return 'ri-calendar-event-fill me-1';
        case 'system'       : return 'ri-question-line me-1';
        case 'text'         : return undefined;
                default        : return undefined;
    }
}

/**
 * Texto curto de uma mensagem citada, montado a partir da mensagem do FRONT. É o da
 * prévia acima do campo de digitação e o da bolha otimista de "Responder em
 * particular" — os dois têm de mostrar a mesma coisa.
 */
export function GetReplySnippet(message: Message) {
    switch (message.type) {
        case 'text'         : return message.text;
        case 'sticker'      : return 'Figurinha';
        case 'list'         : return message.listHeader || 'Lista';
        case 'list-answer'  : return 'Você não deveria ser capaz de responder esse tipo de mensagem T-T';
        case 'audio'        : return message.fileData?.caption || 'Áudio';
        case 'system'       : return 'Sistema';
        case 'poll'         : return 'Enquete';
        case 'file'         : return message.fileData?.caption || message.fileData?.name || 'Arquivo';
        case 'contact'      : return 'Contato';
        case 'imagem'       : return message.fileData?.caption || 'Imagem';
        case 'video'        : return message.fileData?.caption || 'Vídeo';
        case 'location'     : return 'Localização';
        case 'multi_contact': return `${message.contactData?.length} contatos`;
    }
    return message.text;
}

export function TimeStrToChatListTime(timeString: string) {
    return FormatTime(timeString);
}

/** Grupo silenciado pelo usuário e com o silêncio ainda valendo. */
export function grupoEstaSilenciado(chat: Pick<Chat, 'isGroup' | 'isMuted' | 'silenciadoAte'>): boolean {
    if (!chat.isGroup || !chat.isMuted) return false;
    return !chat.silenciadoAte || new Date(chat.silenciadoAte).getTime() > Date.now();
}

export function FormatTime(time: string|Date) {
    if (!time) return time;
    if (typeof time == 'string' && /.+às.+/.test(time)) return time;
    if (typeof time == 'string' && /^\d\d:\d\d$/.test(time)) return time;
    const today = new Date();
    const getTimeHM = (date: Date) => date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', }).match(/\d{2}:\d{2}/)?.at(0);
    const getDateDMY = (date: Date) => date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const date = new Date(time);
    return today.getDate() == date.getDate() ? `${getTimeHM(date)}` : `${getDateDMY(date)} às ${getTimeHM(date)}`
    // return today.getDate() == date.getDate() ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} às ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function pastelColorFromString(seed: string) {
  // Hash the string into a consistent integer
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate H, S, L values (pastel ≈ high lightness + medium/high saturation)
  const h = Math.abs(hash) % 380;     // hue [0–360]
  const s = 70 + (Math.abs(hash) % 10); // saturation [70–80]
  const l = 60 + (Math.abs(hash) % 10); // lightness  [80–90]

  return hslToHex(h, s, l);
}

// Convert HSL → HEX
function hslToHex(h: number, s: number, l: number) {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16).padStart(2, "0");
    return hex;
  };

  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

export function ParseJsonStringOrReturnAsIs(input: undefined|null|string|number|object): undefined|null|string|number|object {
    let output = input;
    try {
        if (typeof input == "string")
        output = JSON.parse(input) as object;
    } catch (e) {
        console.warn('[ParseJsonStringOrReturnAsIs] failed parsing input:');
        console.warn(e);
    }
    return output;
}
